import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Dialog,
    SwipeableDrawer,
    useMediaQuery,
    useTheme,
    Chip,
    FormLabel,
    Paper,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Close from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { aiGenerationAPI } from '../../../services/merchantAPI';

// Custom tags: match GenerationHistory.js inline editor (compact style)
const TagsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '6px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: 'white',
    height: 'auto',
    minHeight: '40px',
    alignItems: 'center',
    boxSizing: 'border-box',
    '&:focus-within': {
        borderColor: '#6318C6',
        boxShadow: '0 0 0 1px #6318C6'
    }
}));

const TagInput = styled('input')({
    height: 'auto !important',
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    fontWeight: '500',
    color: 'black',
    flex: 1,
    minWidth: '100px',
    background: 'transparent',
    padding: '0px 0px!important',
    fontFamily: 'var(--font-montserrat-medium) !important',
    '&::placeholder': {
        color: '#9CA3AF',
        fontWeight: '500'
    }
});

const StyledTagChip = styled(Chip)(({ theme }) => ({
    backgroundColor: '#651BC6',
    border: 'none',
    color: '#fff',
    fontWeight: '500',
    height: '30px',
    borderRadius: '100px',
    fontSize: '13px',
    fontFamily: 'var(--font-montserrat-medium) !important',
    '& .MuiChip-label': {
        paddingLeft: '12px',
        paddingRight: '12px',
    },
    '& .MuiChip-deleteIcon': {
        color: '#fff',
        fontSize: '15px',
        marginRight: '4px',
        '&:hover': {
            color: '#EDE9FE'
        }
    }
}));

/** Parse stored custom_tags (array, comma string, or missing) into a string list. */
function parseTagsFromItem(generationItem) {
    if (!generationItem) return [];
    const raw = generationItem.custom_tags;
    if (Array.isArray(raw)) return raw.map((t) => String(t).trim()).filter(Boolean);
    if (raw != null && raw !== '') {
        return String(raw).split(',').map((t) => t.trim()).filter(Boolean);
    }
    return [];
}

const GenerationInfoPopup = ({ open, onClose, item, PRIMARY_GRADIENT, onRegenerate, onCustomTagsSaved }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [savingTags, setSavingTags] = useState(false);
    const [tagSaveError, setTagSaveError] = useState('');
    const [tagSaveOk, setTagSaveOk] = useState(false);
    const [isTagEditing, setIsTagEditing] = useState(false); // toggle edit mode
    const [customTagSuggestions, setCustomTagSuggestions] = useState([]);
    const [isTagSuggestionsLoading, setIsTagSuggestionsLoading] = useState(false);
    const [isTagInputFocused, setIsTagInputFocused] = useState(false);
    const minTagSuggestChars = 2;
    const latestTagSuggestionRequestRef = useRef(0);
    // Cache suggestions per normalized query to reduce duplicate API calls.
    const customTagSuggestCacheRef = useRef(new Map());
    // Cancel in-flight request when user keeps typing.
    const customTagSuggestAbortRef = useRef(null);

    useEffect(() => {
        if (open && item) {
            setTags(parseTagsFromItem(item));
            setTagInput('');
            setIsTagEditing(false); // always start in read-only mode
            setCustomTagSuggestions([]);
            setIsTagSuggestionsLoading(false);
            setIsTagInputFocused(false);
            customTagSuggestCacheRef.current = new Map();
            if (customTagSuggestAbortRef.current) {
                customTagSuggestAbortRef.current.abort();
                customTagSuggestAbortRef.current = null;
            }
        }
    }, [open, item?.id, item?.custom_tags]);

    /** Same as AI Generation wizard: commit draft as chip(s), comma-separated, skip duplicates. */
    const commitTagInputValue = useCallback((rawValue) => {
        const value = String(rawValue ?? '').trim();
        if (!value) return;
        
        // Split by comma and filter out empty strings
        const newTags = value.split(',').map((t) => t.trim()).filter(Boolean);
        
        setTags((prev) => {
            const unique = newTags.filter((t) => !prev.includes(t));
            if (unique.length === 0) return prev;
            return [...prev, ...unique];
        });
        
        // Clear input after committing
        setTagInput('');
    }, []);

    const handleTagInputKeyDown = useCallback(
        (e) => {
            // Support Enter, Comma, Tab, and Space as delimiters
            if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ' ') {
                if (tagInput.trim()) {
                    e.preventDefault();
                    commitTagInputValue(tagInput);
                    setCustomTagSuggestions([]);
                } else if (e.key === ',') {
                    // Prevent entering just a comma if input is empty
                    e.preventDefault();
                }
            } else if (e.key === 'Backspace' && !tagInput) {
                setTags((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
            } else if (e.key === 'Escape') {
                setCustomTagSuggestions([]);
            }
        },
        [tagInput, commitTagInputValue]
    );

    const handleEditTag = useCallback((tagToEdit) => {
        // Remove from list and put back in input for editing
        setTags((prev) => prev.filter((t) => t !== tagToEdit));
        setTagInput(tagToEdit);
    }, []);

    const handleTagSuggestionSelect = useCallback((suggestedTag) => {
        commitTagInputValue(suggestedTag);
        setCustomTagSuggestions([]);
    }, [commitTagInputValue]);

    // Fetch custom-tag suggestions only while user is actively typing in the input.
    useEffect(() => {
        const query = tagInput.trim();
        if (!isTagInputFocused || query.length < minTagSuggestChars) {
            setCustomTagSuggestions([]);
            setIsTagSuggestionsLoading(false);
            if (customTagSuggestAbortRef.current) {
                customTagSuggestAbortRef.current.abort();
                customTagSuggestAbortRef.current = null;
            }
            return undefined;
        }

        const normalizedQuery = query.toLowerCase();
        const cachedSuggestions = customTagSuggestCacheRef.current.get(normalizedQuery);
        if (cachedSuggestions) {
            const existingTagSet = new Set(tags.map((tag) => String(tag).trim().toLowerCase()));
            setCustomTagSuggestions(
                cachedSuggestions.filter((tag) => !existingTagSet.has(String(tag).trim().toLowerCase()))
            );
            setIsTagSuggestionsLoading(false);
            return undefined;
        }

        const requestId = latestTagSuggestionRequestRef.current + 1;
        latestTagSuggestionRequestRef.current = requestId;

        const debounceId = setTimeout(async () => {
            try {
                setIsTagSuggestionsLoading(true);
                if (customTagSuggestAbortRef.current) {
                    customTagSuggestAbortRef.current.abort();
                }
                const abortController = new AbortController();
                customTagSuggestAbortRef.current = abortController;
                const { data } = await aiGenerationAPI.getCustomTagSuggestions(
                    { q: query, limit: 8 },
                    { signal: abortController.signal }
                );
                if (requestId !== latestTagSuggestionRequestRef.current) return;
                const fetchedSuggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
                customTagSuggestCacheRef.current.set(normalizedQuery, fetchedSuggestions);
                const existingTagSet = new Set(tags.map((tag) => String(tag).trim().toLowerCase()));
                const filteredSuggestions = fetchedSuggestions.filter((tag) => {
                    const normalizedTag = String(tag).trim().toLowerCase();
                    return normalizedTag && !existingTagSet.has(normalizedTag);
                });
                setCustomTagSuggestions(filteredSuggestions);
            } catch (error) {
                if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
                if (requestId !== latestTagSuggestionRequestRef.current) return;
                setCustomTagSuggestions([]);
            } finally {
                if (requestId === latestTagSuggestionRequestRef.current) {
                    setIsTagSuggestionsLoading(false);
                }
            }
        }, 250);

        return () => {
            clearTimeout(debounceId);
            if (customTagSuggestAbortRef.current) {
                customTagSuggestAbortRef.current.abort();
                customTagSuggestAbortRef.current = null;
            }
        };
    }, [tagInput, isTagInputFocused, tags, minTagSuggestChars]);

    const handleRemoveTag = useCallback((tagToRemove) => {
        setTags((prev) => prev.filter((t) => t !== tagToRemove));
    }, []);

    // Merge pending draft input with existing chips so Save includes unsaved typed text.
    const getTagsWithDraftInput = useCallback(() => {
        const draft = String(tagInput ?? '').trim();
        if (!draft) return tags;
        const draftTags = draft.split(',').map((t) => t.trim()).filter(Boolean);
        if (draftTags.length === 0) return tags;
        const existing = new Set(tags.map((t) => String(t).trim().toLowerCase()));
        const merged = [...tags];
        draftTags.forEach((t) => {
            const normalized = t.toLowerCase();
            if (!existing.has(normalized)) {
                merged.push(t);
                existing.add(normalized);
            }
        });
        return merged;
    }, [tags, tagInput]);

    const handleRegenerateClick = useCallback(() => {
        if (!item || typeof onRegenerate !== 'function') return;
        // Pass merged generation so URL prefill uses edited tags (same shape as wizard).
        onRegenerate({ ...item, custom_tags: getTagsWithDraftInput() });
    }, [item, onRegenerate, getTagsWithDraftInput]);

    const handleSaveCustomTags = useCallback(async () => {
        if (!item?.id) return;
        const finalTags = getTagsWithDraftInput();
        setTagSaveError('');
        setTagSaveOk(false);
        setSavingTags(true);
        try {
            const { data } = await aiGenerationAPI.patchGenerationCustomTags(item.id, {
                custom_tags: finalTags,
            });
            // Keep local chips aligned with what we attempted to persist.
            setTags(finalTags);
            setTagInput('');
            const saved = data?.custom_tags ?? finalTags.join(',');
            onCustomTagsSaved?.(item.id, saved);
            setTagSaveOk(true);
            setTimeout(() => setTagSaveOk(false), 2500);
        } catch (e) {
            const msg = e.response?.data?.error || e.message || 'Failed to save tags';
            setTagSaveError(msg);
        } finally {
            setSavingTags(false);
        }
    }, [item, getTagsWithDraftInput, onCustomTagsSaved]);

    if (!item) return null;

    const headerContent = (
        <Box sx={{
            p: { xs: 2.5, md: 3.5 },
            pt: { xs: 1, md: 2.5 },
            borderBottom: '1.2px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'white',
            zIndex: 10
        }}>
            <Typography sx={{
                fontSize: '17.17px',
                fontFamily: 'var(--font-montserrat-semibold) !important',
                fontWeight: 600,
                color: '#000000'
            }}>
                AI Attributes
            </Typography>
            <IconButton onClick={onClose} sx={{ p: 0 }}>
                <img src="/assets/img/icons/button_cl.svg" alt="close" style={{ width: 30, height: 30 }} />
            </IconButton>
        </Box>
    );

    const footerContent = (
        <Box sx={{
            p: { xs: 2.5, md: 3 },
            bgcolor: 'white',
            borderTop: '1.2px solid #E5E7EB',
            boxShadow: '0px -3.82px 11.45px 0px rgba(0, 0, 0, 0.05)',
            zIndex: 10
        }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: { xs: 1.5, md: 3 } }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleRegenerateClick}
                    sx={{
                        background: PRIMARY_GRADIENT,
                        boxShadow: 'none',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: '10px !important',
                        py: 1.6,
                        height: '45px !important',
                        fontSize: '15px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-montserrat-semibold) !important',
                        '&:hover': {
                            background: 'linear-gradient(90deg, #9C52DB 0%, #EAA24A 100%)',
                            opacity: 0.9,
                            boxShadow: 'none'
                        }
                    }}
                >
                    Regenerate with same settings
                </Button>
                {/* Commented as requested: hide Edit Parameters option in AI Attributes popup */}
                {/*
                <Button
                    variant="text"
                    fullWidth
                    sx={{
                        color: '#651BC6',
                        textTransform: 'none',
                        fontSize: '13.36px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-montserrat-semibold) !important'
                    }}
                >
                    Edit Parameters
                </Button>
                */}
            </Box>
        </Box>
    );

    const scrollableContent = (
        <Box sx={{
            px: { xs: 2.5, md: 3.5 },
            py: 2,
            overflowY: 'auto',
            flex: 1,
            maxHeight: isMobile ? 'calc(85vh - 200px)' : '450px'
        }}>
            {/* Product Details */}
            <Box sx={{ pb: '3px', borderBottom: '1.2px solid #E5E7EB', mb: '14px' }}>
                <SectionHeader title="Product Details" />
                <DetailRow label="Choose Jewellery Type" value={item.jewelry_category?.display_name || 'N/A'} />
                <DetailRow label="Output Format" value={item.output_format?.name || (item.file_type === 'video' ? 'Video' : 'Image')} />
                <DetailRow label="Gender" value={item.gender_option?.display_name || 'N/A'} />
            </Box>

            {/* Model Settings */}
            <Box sx={{ pb: '3px', borderBottom: '1.2px solid #E5E7EB', mb: '14px' }}>
                <SectionHeader title="Model Settings" />
                <DetailRow label="Model Type" value={item.model_type?.display_name || 'N/A'} />
                {/* Commented as requested: hide Product Image in AI Attributes popup (can expose internal filenames/paths). */}
                {/*
                <DetailRow 
                    label="Product Image" 
                    value={(item.original_image_path || item.input_image_path || item.original_filename || '').split('/').pop() || 'N/A'} 
                />
                */}
            </Box>

            {/* Appearance */}
            <Box sx={{ pb: '3px', borderBottom: '1.2px solid #E5E7EB', mb: '14px' }}>
                <SectionHeader title="Appearance" />
                <DetailRow label="Ethnicity" value={item.ethnicity?.display_name || 'N/A'} />
                <DetailRow label="Clothing Type" value={item.clothing_type?.display_name || 'N/A'} />
                <DetailRow
                    label="Bust Color"
                    value={item.bust_color?.display_name || 'N/A'}
                    colorCircle={item.bust_color?.color_code}
                />
                <DetailRow label="Bust Finish" value={item.bust_finish?.display_name || 'N/A'} />
            </Box>

            {/* Output Settings */}
            <Box sx={{ pb: 2 }}>
                <SectionHeader title="Output Settings" />
                {/* Prefer media_quality (new field) over output_quality (legacy; often shows 'STANDARD') */}
                <DetailRow
                    label="Media Quality"
                    value={
                        item?.media_quality?.display_name ||
                        item?.media_quality?.name ||
                        item?.media_quality_id ||
                        item?.media_quality ||
                        item?.output_quality ||
                        'N/A'
                    }
                />
                {/* Captions: mirror history preview so users see text that will prefill on regenerate */}
                <DetailRow label="Caption 1" value={item.caption_text_1 || 'N/A'} />
                <DetailRow label="Caption 2" value={item.caption_text_2 || 'N/A'} />

                {/* Custom Tags — read-only with edit icon, matching GenerationHistory pattern */}
                <Box display="flex" alignItems="center" gap={0.5} mt={1} mb={0.5}>
                    <FormLabel component="legend" className="ab-form-label" sx={{ mb: '0 !important' }}>
                        Custom Tags (Optional)
                    </FormLabel>
                    <Tooltip title="Edit tags" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setIsTagEditing(true);
                                setTagSaveError('');
                                setTagSaveOk(false);
                            }}
                            sx={{ 
                                p: '4px', 
                                color: '#651BC6', 
                                '&:hover': { backgroundColor: '#f3f0ff' },
                                '&:hover svg': { color: '#5514B4' }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {isTagEditing ? (
                    /* ── Editor mode ── */
                    <Box>
                        <Box sx={{ position: 'relative' }}>
                            <TagsContainer>
                                {tags.map((tag, index) => (
                                    <StyledTagChip
                                        key={`${tag}-${index}`}
                                        label={tag}
                                        onClick={() => handleEditTag(tag)}
                                        onDelete={() => handleRemoveTag(tag)}
                                        deleteIcon={<Close />}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                                <TagInput
                                    placeholder={tags.length === 0 ? 'Search or Add Tags' : ''}
                                    value={tagInput}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val.endsWith(',') || val.endsWith(' ')) {
                                            const tag = val.slice(0, -1).trim();
                                            if (tag) {
                                                const existing = new Set(tags.map(t => t.toLowerCase()));
                                                if (!existing.has(tag.toLowerCase())) {
                                                    setTags(prev => [...prev, tag]);
                                                }
                                            }
                                            setTagInput('');
                                        } else {
                                            setTagInput(val);
                                        }
                                    }}
                                    onFocus={() => setIsTagInputFocused(true)}
                                    onBlur={() => setIsTagInputFocused(false)}
                                    onKeyDown={handleTagInputKeyDown}
                                    autoFocus
                                />
                            </TagsContainer>
                            {isTagInputFocused && tagInput.trim() && customTagSuggestions.length > 0 && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        position: 'absolute',
                                        top: 'calc(100% + 6px)',
                                        left: 0,
                                        right: 0,
                                        zIndex: 20,
                                        borderRadius: '8px',
                                        border: '1px solid #E5E7EB',
                                        maxHeight: '180px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    {isTagSuggestionsLoading ? (
                                        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                                            <CircularProgress size={18} />
                                        </Box>
                                    ) : (
                                        customTagSuggestions.map((suggestion) => (
                                            <Box
                                                key={suggestion}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => handleTagSuggestionSelect(suggestion)}
                                                sx={{
                                                    px: 1.5,
                                                    py: 1,
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    '&:last-of-type': { borderBottom: 'none' },
                                                    '&:hover': { backgroundColor: '#F9FAFB' }
                                                }}
                                            >
                                                {suggestion}
                                            </Box>
                                        ))
                                    )}
                                </Paper>
                            )}
                        </Box>
                        <Typography sx={{ fontSize: '11px', mt: 0.5, fontWeight: 400, color: '#6B7280' }}>
                            *Add comma after each tag.
                        </Typography>
                        {tagSaveError && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {tagSaveError}
                            </Typography>
                        )}
                        {tagSaveOk && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.main' }}>
                                Tags saved
                            </Typography>
                        )}
                        <Box display="flex" gap={1} mt={1}>
                            <Button
                                type="button"
                                size="small"
                                variant="contained"
                                disabled={savingTags}
                                onClick={handleSaveCustomTags}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    background: PRIMARY_GRADIENT,
                                    boxShadow: 'none',
                                    fontSize: '12px',
                                    py: 0.5,
                                    px: 2,
                                    fontFamily: 'var(--font-montserrat-semibold) !important',
                                    fontWeight: 600,
                                    '&:hover': { opacity: 0.9, boxShadow: 'none' },
                                    '&.Mui-disabled': {
                                        background: 'linear-gradient(90deg, #c4b5e0 0%, #e8d4a8 100%)',
                                        color: 'rgba(255,255,255,0.92)',
                                    },
                                }}
                            >
                                {savingTags ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setIsTagEditing(false)}
                                sx={{ textTransform: 'none', fontSize: '12px', color: '#6B7280', py: 0.5 }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    /* ── Read-only mode ── */
                    <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center" minHeight={28}>
                        {tags.length === 0 ? (
                            <Typography sx={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'var(--font-montserrat-medium)' }}>
                                No tags added
                            </Typography>
                        ) : (
                            tags.map((tag, i) => (
                                <StyledTagChip key={`ro-${tag}-${i}`} label={tag} />
                            ))
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                onOpen={() => { }}
                disableSwipeToOpen={true}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: '30px',
                        borderTopRightRadius: '30px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                {/* Swipe Indicator */}
                <Box
                    onClick={onClose}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 1.5,
                        pb: 0,
                        cursor: 'pointer'
                    }}
                >
                    <Box sx={{
                        width: 40,
                        height: 5,
                        bgcolor: 'rgba(101, 27, 198, 0.4)', // Slightly purple as per placeholder in screenshot?
                        borderRadius: 3,
                        opacity: 0.5
                    }} />
                </Box>
                {headerContent}
                {scrollableContent}
                {footerContent}
            </SwipeableDrawer>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    maxWidth: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh'
                }
            }}
        >
            {headerContent}
            {scrollableContent}
            {footerContent}
        </Dialog>
    );
};

const SectionHeader = ({ title, sx }) => (
    <Typography sx={{
        fontSize: '15.27px',
        fontFamily: 'var(--font-montserrat-semibold) !important',
        fontWeight: 600,
        color: '#000000',
        mb: 2,
        ...sx
    }}>
        {title}
    </Typography>
);

const DetailRow = ({ label, value, colorCircle }) => (
    <Box display="flex" justifyContent="space-between" mb={1.5} alignItems="center">
        <Typography sx={{
            fontSize: '13.36px',
            fontFamily: 'var(--font-montserrat-medium) !important',
            color: '#4A5565',
            fontWeight: 500
        }}>
            {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
            {colorCircle && (
                <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: colorCircle,
                    border: '1px solid rgba(0,0,0,0.1)'
                }} />
            )}
            <Typography sx={{
                fontSize: '13.36px',
                fontFamily: 'var(--font-montserrat-semibold) !important',
                color: '#651BC6',
                fontWeight: 600
            }}>
                {value}
            </Typography>
        </Box>
    </Box>
);

export default GenerationInfoPopup;
